import React, { useState, useEffect, useId, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Loader from '../components/Loader'
import { login } from '../actions/userActions'
import { ToastContext } from '../App'
import { useTranslation } from 'react-i18next'
import '../css/KlerkLoginPage.css'

function EyeOpen() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
			<circle cx="12" cy="12" r="3" />
		</svg>
	)
}

function EyeOff() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
			<path d="M3 3l18 18" />
			<path d="M10.6 5.1A10.6 10.6 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.3 4M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a10.4 10.4 0 0 0 4.2-.9" />
			<path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
		</svg>
	)
}

function Arrow() {
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
			<path d="M5 12h14M13 6l6 6-6 6" />
		</svg>
	)
}

function Login() {
	const uid = useId()
	const emailId = `email-${uid}`
	const passwordId = `password-${uid}`
	const formErrorId = `login-error-${uid}`

	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [touched, setTouched] = useState({})

	const dispatch = useDispatch()
	const navigate = useNavigate()
	const showToast = useContext(ToastContext)

	const { t, i18n } = useTranslation()
	const isFr = i18n.resolvedLanguage === 'fr'

	const toggleLanguage = () => {
		const nextLang = isFr ? 'en' : 'fr'
		i18n.changeLanguage(nextLang)
		localStorage.setItem('lang', nextLang)
	}

	useEffect(() => {
		const savedLang = localStorage.getItem('lang')
		if (savedLang) i18n.changeLanguage(savedLang)
	}, [])

	const userLogin = useSelector((state) => state.userLogin)
	const { error, loading, userInfo } = userLogin

	useEffect(() => {
		if (userInfo) navigate('/homepage')
	}, [userInfo])

	useEffect(() => {
		if (error) showToast?.(error, 'error')
	}, [error, showToast])

	const submitHandler = (e) => {
		e.preventDefault()
		dispatch(login(email, password))
	}

	return (
		<>
			{/* Load fonts */}


			{loading ? (
				<Loader />
			) : (
				<div className="klerk-stage">
					{/* Language toggle */}
					<button
						type="button"
						className={`lang-switch ${isFr ? 'fr' : 'en'}`}
						onClick={toggleLanguage}
						style={{ position: 'fixed', top: '1.25rem', right: '1.25rem', zIndex: 10 }}
					>
						<span className="lang-switch-circle">{isFr ? 'FR' : 'EN'}</span>
					</button>

					<div className="klerk-card">
						{/* Logo */}
						<div className="klerk-logoLogin">
							<img src="/klerk-logo.png" alt="Klerk" />
						</div>

						<h1 className="klerk-headline">{t('login.header')}</h1>
						<p className="klerk-sub">{t('login.clickingsignupbutton')}</p>

						<form onSubmit={submitHandler} noValidate>
							{error && (
								<p id={formErrorId} className="klerk-err klerk-err--form" role="alert">
									{error}
								</p>
							)}

							{/* Email */}
							<div className="klerk-field">
								<label htmlFor={emailId}>
									{t('login.username', 'Email')}
								</label>
								<div className="klerk-input-shell">
									<input
										id={emailId}
										name="email"
										type="email"
										placeholder="you@yourshop.com"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										onBlur={() => setTouched(s => ({ ...s, email: true }))}
										required
										autoComplete="email"
									/>
								</div>
							</div>

							{/* Password */}
							<div className="klerk-field">
								<div className="klerk-field-head">
									<label htmlFor={passwordId}>
										{t('login.password')}
									</label>
									<Link to="/forgot-password/" className="klerk-link">
										{t('login.ForgotPassword')}
									</Link>
								</div>
								<div className="klerk-input-shell">
									<input
										id={passwordId}
										name="password"
										type={showPassword ? 'text' : 'password'}
										className="klerk-pw"
										placeholder="••••••••"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										onBlur={() => setTouched(s => ({ ...s, password: true }))}
										required
										autoComplete="current-password"
									/>
									<button
										type="button"
										className="klerk-peek"
										aria-label={showPassword ? 'Hide password' : 'Show password'}
										onClick={() => setShowPassword(s => !s)}
									>
										{showPassword ? <EyeOff /> : <EyeOpen />}
									</button>
								</div>
							</div>

							<button className="klerk-btn" type="submit" disabled={loading}>
								{t('login.header')} <Arrow />
							</button>
						</form>

						<p className="klerk-signup">
							{t('login.createAccount')}{' '}
							<Link to="/sign-up/" className="klerk-link klerk-link--strong">
								{t('login.Signup')}
							</Link>
						</p>
					</div>

					<div className="klerk-foot">
						<span>© {new Date().getFullYear()} Klerk</span>
						<a href="#">Privacy</a>
						<a href="#">Terms</a>
						<a href="#">Support</a>
					</div>
				</div>
			)}
		</>
	)
}

export default Login